import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PAS SEKCJI — nazwany dział ekranu, przeniesiony z `.pas-sekcji` makiety
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * PO CO ISTNIEJE. Ekrany produktu były jednym `<Stack spacing={3}>`: każdy blok tej samej wagi,
 * ten sam odstęp, żadnego podziału. Makieta dzieli je na NAZWANE DZIAŁY — „Sieć w liczbach",
 * „Ranking dostawców", „Wynik 30 dni", „Anomalie" — i to one, a nie tytuły kart, mówią, po co
 * czyta się dalej. Bez nich długi ekran jest listą pudełek, po której trzeba wodzić wzrokiem.
 *
 * DLACZEGO STOPIEŃ JEST WIĘKSZY NIŻ TYTUŁ KARTY. Nagłówek działu (`--st-duzy`, krój display,
 * WERSALIKI) ma być czytelny z odległości przewijania. Produkt używał tu `variant="h5"` = 24 px
 * Segoe UI zapisem zdaniowym — czyli TEGO SAMEGO rejestru co tytuł ekranu, przez co dział
 * konkurował z tytułem zamiast się pod niego podporządkować.
 *
 * OPIS JEST OGRANICZONY DO 64ch — tak jak w makiecie. Dłuższa linia przestaje się skanować
 * i zamienia dział w akapit.
 *
 * ── DWA WARIANTY, BO MAKIETA MA DWA POZIOMY PODZIAŁU ──────────────────────────────────────────
 *
 * `pas`   — głośny dział (`--st-duzy`, krój display). Dzieli ekran na części, które odpowiadają
 *           na różne pytania. Domyślny.
 *
 * `cichy` — cichy nagłówek sekcji z kreską dociągniętą do prawej krawędzi. Makieta ma go pod
 *           czterema nazwami klas na czterech ekranach (`.sprz-sekcja`, `.dostawy-sekcja` i
 *           dalej) z tą samą recepturą: krój marki 900, `--st-mikro`, rozstrzelenie 0.16em,
 *           wersaliki, kolor tekstu słabego. Jego zadaniem jest PORZĄDKOWAĆ PRZEWIJANIE, a nie
 *           konkurować stopniem z liczbą-bohaterem — dlatego jest mniejszy od tytułu karty,
 *           choć stoi wyżej w strukturze. Kreska zastępuje ramkę wokół grupy kart.
 *
 * Ranga nagłówka (`h2`/`h3`) jest NIEZALEŻNA od wariantu. Wariant to waga optyczna, ranga to
 * struktura dokumentu — zlanie tych dwóch rzeczy jest dokładnie tym, przez co produkt miał
 * przeskoki `h1 → h5`.
 */

export function PasSekcji({
  tytul,
  opis,
  akcja,
  poziom = 'h2',
  wariant = 'pas',
}: {
  tytul: ReactNode;
  opis?: ReactNode;
  /** Element wypchnięty do prawej krawędzi pasa (np. wybór wariantu widoku). */
  akcja?: ReactNode;
  poziom?: 'h2' | 'h3';
  wariant?: 'pas' | 'cichy';
}) {
  const cichy = wariant === 'cichy';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: cichy ? 'center' : 'baseline',
        gap: cichy ? 'var(--o3)' : 1.5,
        flexWrap: 'wrap',
        // Odstęp z makiety: duży NAD działem, mały pod nim — dział należy do tego, co po nim,
        // a nie do tego, co przed. Pierwsza wersja z równym odstępem czytała się odwrotnie.
        mt: 4,
        mb: cichy ? 'var(--o3)' : 1.5,
      }}
    >
      <Typography
        component={poziom}
        sx={
          cichy
            ? {
                fontFamily: 'var(--krój-marka)',
                fontWeight: 900,
                fontSize: 'var(--st-mikro)',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--tekst-slaby)',
                whiteSpace: 'nowrap',
              }
            : {
                fontFamily: 'var(--krój-display)',
                fontSize: 'var(--st-duzy)',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }
        }
      >
        {tytul}
      </Typography>
      {opis ? (
        <Typography
          sx={{ m: 0, maxWidth: '64ch', fontSize: 'var(--st-drobny)', color: 'var(--tekst-slaby)' }}
        >
          {opis}
        </Typography>
      ) : null}

      {/* `.sprz-sekcja::after` z makiety. Kreska jest DEKORACJĄ — stąd aria-hidden; czytnik
          ekranu dostaje sam nagłówek. Jest też rozpychaczem, więc w wariancie cichym akcja
          nie potrzebuje osobnego `flexGrow`. */}
      {cichy ? (
        <Box aria-hidden sx={{ flex: 1, height: '1px', minWidth: '2ch', background: 'var(--linia)' }} />
      ) : null}

      {akcja ? (
        <>
          {cichy ? null : <Box sx={{ flexGrow: 1 }} />}
          {akcja}
        </>
      ) : null}
    </Box>
  );
}
